import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GitCompareArrows, Minus, Plus, Equal } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface Props {
  summaryA: string;
  summaryB: string;
  modelA?: string;
  modelB?: string;
}

interface DiffSection {
  title: string;
  contentA: string;
  contentB: string;
  similarity: number;
}

const SECTION_HEADERS = [
  "Executive Overview",
  "Core Arguments",
  "Key Data",
  "Deep Analysis",
  "Strategic Takeaways",
  "Critical Assessment",
  "Classification",
];

function extractSections(text: string): Record<string, string> {
  const sections: Record<string, string> = {};
  for (let i = 0; i < SECTION_HEADERS.length; i++) {
    const header = SECTION_HEADERS[i];
    const regex = new RegExp(`#+\\s*(?:(?:\\d+\\.?\\s*)?(?:📋|💡|📊|🔬|🎯|⚠️|🏷️|\\S*)?\\s*)?${header.replace(/\s+/g, "\\s+")}`, "i");
    const match = text.match(regex);
    if (!match) continue;
    const startIdx = match.index! + match[0].length;
    let endIdx = text.length;
    for (let j = i + 1; j < SECTION_HEADERS.length; j++) {
      const nextRegex = new RegExp(`#+\\s*(?:(?:\\d+\\.?\\s*)?(?:📋|💡|📊|🔬|🎯|⚠️|🏷️|\\S*)?\\s*)?${SECTION_HEADERS[j].replace(/\s+/g, "\\s+")}`, "i");
      const nextMatch = text.substring(startIdx).match(nextRegex);
      if (nextMatch) {
        endIdx = startIdx + nextMatch.index!;
        break;
      }
    }
    sections[header] = text.substring(startIdx, endIdx).trim();
  }
  return sections;
}

function computeSimilarity(a: string, b: string): number {
  if (!a && !b) return 1;
  if (!a || !b) return 0;
  const wordsA = new Set(a.toLowerCase().split(/\s+/).filter(w => w.length > 3));
  const wordsB = new Set(b.toLowerCase().split(/\s+/).filter(w => w.length > 3));
  if (wordsA.size === 0 && wordsB.size === 0) return 1;
  let intersection = 0;
  wordsA.forEach(w => { if (wordsB.has(w)) intersection++; });
  return intersection / Math.max(wordsA.size, wordsB.size);
}

function findUniquePoints(text: string, otherText: string): string[] {
  const sentences = text.split(/[.!?]\s+/).filter(s => s.trim().length > 20);
  const otherLower = otherText.toLowerCase();
  return sentences
    .filter(s => {
      const keywords = s.toLowerCase().split(/\s+/).filter(w => w.length > 4);
      const matchCount = keywords.filter(k => otherLower.includes(k)).length;
      return keywords.length > 0 && matchCount / keywords.length < 0.4;
    })
    .slice(0, 3);
}

function SimilarityBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const variant = pct > 70 ? "default" : pct > 40 ? "secondary" : "outline";
  const icon = pct > 70 ? <Equal className="h-3 w-3" /> : pct > 40 ? <GitCompareArrows className="h-3 w-3" /> : <Minus className="h-3 w-3" />;
  return (
    <Badge variant={variant} className="text-[10px] gap-1">
      {icon} {pct}% similar
    </Badge>
  );
}

export function SummaryDiffView({ summaryA, summaryB, modelA, modelB }: Props) {
  const diffSections = useMemo(() => {
    const secA = extractSections(summaryA);
    const secB = extractSections(summaryB);
    return SECTION_HEADERS.map(title => ({
      title,
      contentA: secA[title] || "",
      contentB: secB[title] || "",
      similarity: computeSimilarity(secA[title] || "", secB[title] || ""),
    })).filter(s => s.contentA || s.contentB);
  }, [summaryA, summaryB]);

  const overallSimilarity = useMemo(() => {
    if (diffSections.length === 0) return 0;
    return diffSections.reduce((sum, s) => sum + s.similarity, 0) / diffSections.length;
  }, [diffSections]);

  if (diffSections.length === 0) return null;

  return (
    <Card className="border-primary/10">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GitCompareArrows className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Diff Analysis</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Overall:</span>
            <SimilarityBadge value={overallSimilarity} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-[600px]">
          <div className="space-y-4 pr-3">
            {diffSections.map((section) => (
              <DiffSectionCard
                key={section.title}
                section={section}
                modelA={modelA}
                modelB={modelB}
              />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}

function DiffSectionCard({ section, modelA, modelB }: { section: DiffSection; modelA?: string; modelB?: string }) {
  const uniqueA = useMemo(() => findUniquePoints(section.contentA, section.contentB), [section]);
  const uniqueB = useMemo(() => findUniquePoints(section.contentB, section.contentA), [section]);

  return (
    <div className="rounded-lg border border-border/50 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground">{section.title}</h4>
        <SimilarityBadge value={section.similarity} />
      </div>

      {(uniqueA.length > 0 || uniqueB.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {uniqueA.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <Plus className="h-3 w-3 text-emerald-500" />
                Unique to {modelA || "Model A"}
              </p>
              {uniqueA.map((point, i) => (
                <p key={i} className="text-xs text-muted-foreground bg-emerald-500/5 border-l-2 border-emerald-500/30 pl-2 py-1 rounded-r">
                  {point.trim()}
                </p>
              ))}
            </div>
          )}
          {uniqueB.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <Plus className="h-3 w-3 text-blue-500" />
                Unique to {modelB || "Model B"}
              </p>
              {uniqueB.map((point, i) => (
                <p key={i} className="text-xs text-muted-foreground bg-blue-500/5 border-l-2 border-blue-500/30 pl-2 py-1 rounded-r">
                  {point.trim()}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {uniqueA.length === 0 && uniqueB.length === 0 && (
        <p className="text-xs text-muted-foreground italic flex items-center gap-1">
          <Equal className="h-3 w-3" /> Both models produced very similar content for this section.
        </p>
      )}
    </div>
  );
}
